import { useState, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  Layers,
  Plus,
  Trash2,
  Loader2,
  Save,
  Search,
  AlertCircle,
  BarChart3,
  Upload,
  Download,
  User,
  CheckCircle2,
  ArrowDownAZ,
  Clock,
  Flame,
  ListOrdered,
  FileSpreadsheet,
} from 'lucide-react';
import { Tutee } from '../../../types/tuition';
import AnimatedCard from '../../../components/ui/AnimatedCard';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';
import { TUTEE_ICON_MAP } from '../../../utils/tuteeIcons';
import { getStatClasses } from '../../../utils/colorUtils';
import { fetchTuteeComponents, updateTuteeComponentConfig } from '../../../services/componentService';
import {
  fetchFlashcardDecks,
  createFlashcardDeck,
  deleteFlashcardDeck,
  fetchAllFlashcardsForTutee,
  createFlashcard,
  deleteFlashcard,
  fetchHardestCardsForTutee,
  fetchProgressForTutee,
  mergeProgressByCard,
  fetchCardReviewSummariesForTutee,
  parseFlashcardConfig,
  findOrCreateFlashcardDeck,
  bulkCreateFlashcards,
  type FlashcardDeck,
  type Flashcard,
  type FlashcardModuleConfig,
  type FlashcardProgress,
  type CardReviewSummary,
} from '../../../services/flashcardService';
import FlashcardAdminCardItem from './FlashcardAdminCardItem';
import FlashcardStudyHistory from './FlashcardStudyHistory';
import {
  parseFlashcardCsv,
  downloadFlashcardImportTemplate,
  type ParseFlashcardCsvResult,
} from '../../../utils/flashcardCsvUtils';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = TUTEE_ICON_MAP;

const IMPORT_PREVIEW_ROWS = 10;

function resetImportFileInput(input: HTMLInputElement | null) {
  if (input) input.value = '';
}

/** Portal + high z-index so backdrop dims the whole page (including the group sidebar). */
function FlashcardsFormModal({
  open,
  onClose,
  children,
  wide = false,
  extraWide = false,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  extraWide?: boolean;
}) {
  if (!open) return null;
  const widthClass = extraWide ? 'max-w-3xl' : wide ? 'max-w-lg' : 'max-w-md';
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-modal-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`bg-white rounded-2xl p-6 w-full shadow-2xl animate-modal-content border border-white/20 ${widthClass}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

interface FlashcardsConfigProps {
  tutees: Tutee[];
  selectedTuteeId: string;
}

type CardSortMode = 'deck' | 'recent' | 'hardest' | 'most_seen' | 'front_az';

const CARD_SORT_OPTIONS: { id: CardSortMode; label: string; icon: typeof ListOrdered }[] = [
  { id: 'deck', label: 'Deck order', icon: ListOrdered },
  { id: 'recent', label: 'Recent', icon: Clock },
  { id: 'hardest', label: 'Hardest', icon: Flame },
  { id: 'most_seen', label: 'Most seen', icon: BarChart3 },
  { id: 'front_az', label: 'A → Z', icon: ArrowDownAZ },
];

function isCardStudied(
  cardId: string,
  progressByCardId: Map<string, FlashcardProgress>,
  reviewSummaryByCardId: Map<string, CardReviewSummary>
): boolean {
  const summary = reviewSummaryByCardId.get(cardId);
  if ((summary?.reviewCount ?? 0) > 0) return true;
  const progress = progressByCardId.get(cardId);
  if (progress?.lastReviewedAt) return true;
  if ((progress?.reps ?? 0) > 0) return true;
  return false;
}

const FlashcardsConfig = ({ tutees, selectedTuteeId }: FlashcardsConfigProps) => {
  const [cardSearchTerm, setCardSearchTerm] = useState('');
  const [cardSort, setCardSort] = useState<CardSortMode>('deck');
  const [hideNotSeen, setHideNotSeen] = useState(false);
  const [decks, setDecks] = useState<FlashcardDeck[]>([]);
  const [cards, setCards] = useState<Flashcard[]>([]);
  const [selectedDeckId, setSelectedDeckId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [config, setConfig] = useState<FlashcardModuleConfig>(() => parseFlashcardConfig({}));
  const [tuteeCompId, setTuteeCompId] = useState<string | null>(null);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [hardest, setHardest] = useState<Array<{ card: Flashcard; lapses: number }>>([]);
  const [progressByCardId, setProgressByCardId] = useState<Map<string, FlashcardProgress>>(new Map());
  const [reviewSummaryByCardId, setReviewSummaryByCardId] = useState<Map<string, CardReviewSummary>>(
    new Map()
  );
  const [showDeckModal, setShowDeckModal] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [deckName, setDeckName] = useState('');
  const [cardFront, setCardFront] = useState('');
  const [cardBack, setCardBack] = useState('');
  const [deleteDeckId, setDeleteDeckId] = useState<string | null>(null);
  const [deleteCardId, setDeleteCardId] = useState<string | null>(null);
  const [movingCardId, setMovingCardId] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState<ParseFlashcardCsvResult | null>(null);
  const [importFileName, setImportFileName] = useState<string | null>(null);
  const [importDragActive, setImportDragActive] = useState(false);
  const [importSuccess, setImportSuccess] = useState<{ count: number; deckLabel: string } | null>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);
  const [importDeckMode, setImportDeckMode] = useState<'current' | 'new'>('current');
  const [importNewDeckName, setImportNewDeckName] = useState('Imported deck');
  const [isImporting, setIsImporting] = useState(false);

  const selectedTutee = useMemo(
    () => tutees.find((t) => t.id === selectedTuteeId),
    [tutees, selectedTuteeId]
  );

  const load = async () => {
    if (!selectedTuteeId) return;
    try {
      setLoading(true);
      setError('');
      const [deckList, tuteeComps] = await Promise.all([
        fetchFlashcardDecks(selectedTuteeId),
        fetchTuteeComponents(selectedTuteeId),
      ]);
      setDecks(deckList);
      const fc = tuteeComps.find((c) => c.component?.componentType === 'flashcards');
      if (fc) {
        setTuteeCompId(fc.id);
        setConfig(parseFlashcardConfig(fc.config));
      }
      const [allCards, progressList, summaries, hard] = await Promise.all([
        fetchAllFlashcardsForTutee(selectedTuteeId, false),
        fetchProgressForTutee(selectedTuteeId),
        fetchCardReviewSummariesForTutee(selectedTuteeId),
        fetchHardestCardsForTutee(selectedTuteeId, 5),
      ]);
      setCards(allCards);
      setProgressByCardId(mergeProgressByCard(progressList));
      setReviewSummaryByCardId(summaries);
      setHardest(hard);
    } catch (err) {
      console.error(err);
      setError('Failed to load flashcards data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [selectedTuteeId]);

  useEffect(() => {
    setCardSearchTerm('');
    setCardSort('deck');
    setHideNotSeen(false);
  }, [selectedTuteeId, selectedDeckId]);

  const deckFilteredCards = useMemo(() => {
    if (!selectedDeckId) return cards.filter((c) => !c.deckId);
    return cards.filter((c) => c.deckId === selectedDeckId);
  }, [cards, selectedDeckId]);

  const searchFilteredCards = useMemo(() => {
    const q = cardSearchTerm.trim().toLowerCase();
    if (!q) return deckFilteredCards;
    return deckFilteredCards.filter(
      (c) => c.front.toLowerCase().includes(q) || c.back.toLowerCase().includes(q)
    );
  }, [deckFilteredCards, cardSearchTerm]);

  const displayedCards = useMemo(() => {
    let list = searchFilteredCards;
    if (hideNotSeen) {
      list = list.filter((c) =>
        isCardStudied(c.id, progressByCardId, reviewSummaryByCardId)
      );
    }

    const lastSeenMs = (cardId: string) => {
      const summary = reviewSummaryByCardId.get(cardId);
      const progress = progressByCardId.get(cardId);
      const iso = summary?.lastReviewedAt ?? progress?.lastReviewedAt;
      return iso ? new Date(iso).getTime() : 0;
    };

    const sorted = [...list];
    switch (cardSort) {
      case 'recent':
        sorted.sort((a, b) => lastSeenMs(b.id) - lastSeenMs(a.id));
        break;
      case 'hardest': {
        const score = (cardId: string) => {
          const p = progressByCardId.get(cardId);
          const again = reviewSummaryByCardId.get(cardId)?.ratingCounts.again ?? 0;
          return (p?.lapses ?? 0) * 10 + again;
        };
        sorted.sort((a, b) => score(b.id) - score(a.id));
        break;
      }
      case 'most_seen':
        sorted.sort(
          (a, b) =>
            (reviewSummaryByCardId.get(b.id)?.reviewCount ?? 0) -
            (reviewSummaryByCardId.get(a.id)?.reviewCount ?? 0)
        );
        break;
      case 'front_az':
        sorted.sort((a, b) => a.front.localeCompare(b.front, undefined, { sensitivity: 'base' }));
        break;
      case 'deck':
      default:
        sorted.sort((a, b) => a.displayOrder - b.displayOrder || a.front.localeCompare(b.front));
        break;
    }
    return sorted;
  }, [
    searchFilteredCards,
    hideNotSeen,
    cardSort,
    progressByCardId,
    reviewSummaryByCardId,
  ]);

  const hasActiveCardFilters =
    hideNotSeen || cardSort !== 'deck' || cardSearchTerm.trim().length > 0;

  const deckLabel = selectedDeckId
    ? decks.find((d) => d.id === selectedDeckId)?.name ?? 'this deck'
    : 'Unassigned';

  const deckCardIds = useMemo(
    () => deckFilteredCards.map((c) => c.id),
    [deckFilteredCards]
  );

  const handleSaveConfig = async () => {
    if (!tuteeCompId) {
      setError('Assign Flashcards module to this group in Module Manager first.');
      return;
    }
    setIsSavingConfig(true);
    try {
      await updateTuteeComponentConfig(tuteeCompId, config as unknown as Record<string, unknown>);
    } catch (err) {
      console.error(err);
      setError('Failed to save settings.');
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleAddDeck = async () => {
    if (!deckName.trim() || !selectedTuteeId) return;
    await createFlashcardDeck({ tuteeId: selectedTuteeId, name: deckName.trim(), createdBy: 'tutor' });
    setDeckName('');
    setShowDeckModal(false);
    load();
  };

  const handleAddCard = async () => {
    if (!cardFront.trim() || !cardBack.trim() || !selectedTuteeId) return;
    await createFlashcard({
      tuteeId: selectedTuteeId,
      deckId: selectedDeckId || undefined,
      front: cardFront,
      back: cardBack,
    });
    setCardFront('');
    setCardBack('');
    setShowCardModal(false);
    load();
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportPreview(null);
    setImportFileName(null);
    setImportDragActive(false);
    resetImportFileInput(importFileInputRef.current);
  };

  const handleImportFile = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv') && file.type !== 'text/csv') {
      setError('Please upload a CSV file.');
      return;
    }
    setImportFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      setImportPreview(parseFlashcardCsv(text));
    };
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    if (!selectedTuteeId || !importPreview || importPreview.rows.length === 0) return;
    setIsImporting(true);
    setError('');
    try {
      let deckId: string | null = selectedDeckId;
      let deckLabel =
        selectedDeckId
          ? decks.find((d) => d.id === selectedDeckId)?.name ?? 'Selected deck'
          : 'Unassigned';
      if (importDeckMode === 'new') {
        const name = importNewDeckName.trim() || 'Imported deck';
        const deck = await findOrCreateFlashcardDeck(selectedTuteeId, name);
        deckId = deck.id;
        deckLabel = name;
      } else if (!deckId && importDeckMode === 'current') {
        deckId = null;
      }

      const { created } = await bulkCreateFlashcards(
        importPreview.rows.map((row, i) => ({
          tuteeId: selectedTuteeId,
          deckId,
          front: row.front,
          back: row.back,
          displayOrder: i,
        }))
      );

      closeImportModal();
      if (importDeckMode === 'new' && deckId) setSelectedDeckId(deckId);
      await load();
      setImportSuccess({ count: created, deckLabel });
    } catch (err) {
      console.error(err);
      setError('Bulk import failed. Check the file format and try again.');
    } finally {
      setIsImporting(false);
    }
  };

  const handleMoveCard = async (cardId: string, deckId: string | null) => {
    setMovingCardId(cardId);
    try {
      await updateFlashcard(cardId, { deckId });
      await load();
    } catch (err) {
      console.error(err);
      setError('Failed to move card. Please try again.');
    } finally {
      setMovingCardId(null);
    }
  };

  const getIcon = (iconName: string) => iconMap[iconName] || Layers;

  return (
    <div id="flashcards-admin-section">
    <AnimatedCard className="!p-0 overflow-hidden" hover={false}>
      <div className="p-4 sm:p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-xl flex items-center gap-2 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <div className="min-w-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2
                  className={`w-8 h-8 animate-spin ${
                    selectedTutee
                      ? getStatClasses(selectedTutee.colorScheme.primary).text
                      : 'text-indigo-600'
                  }`}
                />
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-2xl">
                  <label className="text-xs font-bold text-gray-500 uppercase">
                    New cards / day
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={config.newCardsPerDay}
                      onChange={(e) =>
                        setConfig({ ...config, newCardsPerDay: Number(e.target.value) || 20 })
                      }
                      className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="text-xs font-bold text-gray-500 uppercase">
                    Max reviews / day
                    <input
                      type="number"
                      min={10}
                      value={config.maxReviewsPerDay}
                      onChange={(e) =>
                        setConfig({ ...config, maxReviewsPerDay: Number(e.target.value) || 200 })
                      }
                      className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="flex items-center gap-2 sm:col-span-2 text-sm font-medium">
                    <input
                      type="checkbox"
                      checked={config.allowStudentCreatedCards}
                      onChange={(e) =>
                        setConfig({ ...config, allowStudentCreatedCards: e.target.checked })
                      }
                    />
                    Allow students to create, edit, and import personal cards
                  </label>
                  <button
                    type="button"
                    onClick={handleSaveConfig}
                    disabled={isSavingConfig || !tuteeCompId}
                    className={`sm:col-span-2 flex items-center justify-center gap-2 py-2.5 rounded-xl text-white font-bold text-sm disabled:opacity-50 ${
                      selectedTutee
                        ? `bg-gradient-to-r ${selectedTutee.colorScheme.gradient}`
                        : 'bg-indigo-600'
                    }`}
                  >
                    {isSavingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save module settings
                  </button>
                </div>

                <div className="flex flex-wrap gap-2 mb-4 items-center">
                  <button
                    type="button"
                    onClick={() => setSelectedDeckId(null)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                      !selectedDeckId && selectedTutee
                        ? `bg-gradient-to-r ${selectedTutee.colorScheme.gradient} text-white`
                        : !selectedDeckId
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    Unassigned
                  </button>
                  {decks.map((d) => {
                    const isDeckActive = selectedDeckId === d.id;
                    return (
                      <div
                        key={d.id}
                        className={`group/deck inline-flex items-center rounded-full text-xs font-bold ${
                          isDeckActive && selectedTutee
                            ? `bg-gradient-to-r ${selectedTutee.colorScheme.gradient} text-white shadow-sm`
                            : isDeckActive
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => setSelectedDeckId(d.id)}
                          className="pl-3 pr-1 py-1.5 rounded-l-full"
                        >
                          {d.name}
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteDeckId(d.id)}
                          aria-label={`Delete deck ${d.name}`}
                          className={`p-1.5 mr-0.5 rounded-full transition-opacity opacity-0 group-hover/deck:opacity-100 focus:opacity-100 ${
                            isDeckActive ? 'hover:bg-white/20' : 'hover:bg-gray-200'
                          }`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setShowDeckModal(true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold border-2 border-dashed border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50 hover:text-gray-800 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Deck
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setImportPreview(null);
                      setImportFileName(null);
                      resetImportFileInput(importFileInputRef.current);
                      setShowImportModal(true);
                    }}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    Import CSV
                  </button>
                </div>

                <div className="flex items-center gap-2 mb-3 w-full border border-gray-200 rounded-xl bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-300">
                  <Search className="w-4 h-4 text-gray-400 shrink-0" aria-hidden />
                  <input
                    value={cardSearchTerm}
                    onChange={(e) => setCardSearchTerm(e.target.value)}
                    placeholder="Search cards in this deck (front or back)…"
                    className="flex-1 min-w-0 border-0 p-0 text-sm bg-transparent outline-none"
                  />
                </div>

                <div className="flex flex-col gap-3 mb-3 p-3 sm:p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider w-full sm:w-auto">
                      Sort
                    </span>
                    {CARD_SORT_OPTIONS.map(({ id, label, icon: Icon }) => {
                      const active = cardSort === id;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setCardSort(id)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-bold transition-all ${
                            active
                              ? selectedTutee
                                ? `bg-gradient-to-r ${selectedTutee.colorScheme.gradient} text-white shadow-sm`
                                : 'bg-indigo-600 text-white'
                              : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3 pt-1 border-t border-gray-100">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={hideNotSeen}
                        onChange={(e) => setHideNotSeen(e.target.checked)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500/30"
                      />
                      Hide not studied
                    </label>
                    {hasActiveCardFilters && (
                      <p className="text-xs text-gray-500 font-medium">
                        Showing {displayedCards.length} of {deckFilteredCards.length} cards
                      </p>
                    )}
                  </div>
                </div>

                <ul className="space-y-3 max-h-[min(42rem,75vh)] overflow-y-auto overscroll-contain mb-6 pr-0.5">
                  {deckFilteredCards.length === 0 && (
                    <li className="text-sm text-gray-500 py-3 text-center">No cards in this deck yet.</li>
                  )}
                  {deckFilteredCards.length > 0 && displayedCards.length === 0 && (
                    <li className="text-sm text-gray-500 py-3 text-center">
                      {hideNotSeen && !cardSearchTerm.trim()
                        ? 'No studied cards in this deck yet.'
                        : 'No cards match your filters.'}
                    </li>
                  )}
                  {displayedCards.map((c) => (
                    <FlashcardAdminCardItem
                      key={c.id}
                      card={c}
                      decks={decks}
                      progress={progressByCardId.get(c.id) ?? null}
                      summary={reviewSummaryByCardId.get(c.id)}
                      selectedTutee={selectedTutee}
                      moving={movingCardId === c.id}
                      onMove={(deckId) => handleMoveCard(c.id, deckId)}
                      onDelete={() => setDeleteCardId(c.id)}
                      onUpdated={load}
                    />
                  ))}
                  <li className="pt-1">
                    <button
                      type="button"
                      onClick={() => setShowCardModal(true)}
                      className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl border-2 border-dashed text-sm font-bold transition-colors ${
                        selectedTutee
                          ? 'border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Plus className="w-4 h-4" />
                      Add card
                      {selectedDeckId
                        ? ` to ${decks.find((d) => d.id === selectedDeckId)?.name ?? 'deck'}`
                        : ' (unassigned)'}
                    </button>
                  </li>
                </ul>

                <FlashcardStudyHistory
                  tuteeId={selectedTuteeId}
                  selectedTutee={selectedTutee}
                  deckCardIds={deckCardIds}
                  deckLabel={deckLabel}
                />

                {hardest.length > 0 && (
                  <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100">
                    <h4 className="text-sm font-black text-amber-900 flex items-center gap-2 mb-2">
                      <BarChart3 className="w-4 h-4" />
                      Hardest cards (lapses)
                    </h4>
                    <ul className="text-sm space-y-1">
                      {hardest.map(({ card, lapses }) => (
                        <li key={card.id} className="text-gray-700">
                          {card.front.slice(0, 40)}… — {lapses} lapses
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
        </div>
      </div>

      <FlashcardsFormModal
        open={showImportModal}
        onClose={closeImportModal}
        extraWide
      >
        <h3 className="font-black text-xl mb-1">Bulk import</h3>
        <p className="text-sm text-gray-600 mb-4">
          Upload a CSV for <strong>{selectedTutee?.name ?? 'this group'}</strong>. Front = English, back =
          Chinese (+ pinyin). Your P5–6 file with{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">Question,Correct Answer</code> columns works as-is.
        </p>
        <button
          type="button"
          onClick={downloadFlashcardImportTemplate}
          className="mb-4 flex items-center gap-2 text-sm font-bold text-indigo-600 hover:text-indigo-800"
        >
          <Download className="w-4 h-4" />
          Download template (front, back)
        </button>

        <input
          ref={importFileInputRef}
          type="file"
          accept=".csv,text/csv"
          className="sr-only"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleImportFile(f);
          }}
        />

        {!importPreview ? (
          <div
            role="button"
            tabIndex={0}
            onClick={() => importFileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') importFileInputRef.current?.click();
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              setImportDragActive(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setImportDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              if (e.currentTarget === e.target) setImportDragActive(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setImportDragActive(false);
              const f = e.dataTransfer.files?.[0];
              if (f) handleImportFile(f);
            }}
            className={`mb-4 rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
              importDragActive
                ? 'border-indigo-400 bg-indigo-50'
                : 'border-gray-200 bg-gray-50 hover:border-indigo-300 hover:bg-indigo-50/50'
            }`}
          >
            <FileSpreadsheet className="w-10 h-10 text-indigo-500 mx-auto mb-3" />
            <p className="font-bold text-gray-800">Drop your CSV here or click to browse</p>
            <p className="text-xs text-gray-500 mt-1">Supports front,back or Question,Correct Answer</p>
          </div>
        ) : (
          <div className="mb-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2 p-3 bg-indigo-50 rounded-xl border border-indigo-100">
              <FileSpreadsheet className="w-4 h-4 text-indigo-600 shrink-0" />
              <span className="text-sm font-bold text-indigo-900 truncate max-w-[12rem] sm:max-w-none">
                {importFileName ?? 'Uploaded file'}
              </span>
              <span className="text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-white text-indigo-700 border border-indigo-100">
                {importPreview.format === 'question_correct_answer' ? 'Question / Answer' : 'Front / Back'}
              </span>
              <span className="text-xs font-bold text-indigo-800 ml-auto">
                {importPreview.rows.length} card{importPreview.rows.length === 1 ? '' : 's'} ready
              </span>
              <button
                type="button"
                onClick={() => {
                  setImportPreview(null);
                  setImportFileName(null);
                  resetImportFileInput(importFileInputRef.current);
                }}
                className="text-xs font-bold text-indigo-600 hover:text-indigo-800 underline"
              >
                Choose different file
              </button>
            </div>

            {importPreview.format === 'question_correct_answer' && (
              <p className="text-xs text-gray-500 px-1">
                Mapped as Correct Answer → front (English), Question → back (Chinese).
              </p>
            )}

            {importPreview.skipped > 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                {importPreview.skipped} row{importPreview.skipped === 1 ? '' : 's'} skipped (empty or incomplete).
              </p>
            )}

            {importPreview.errors.length > 0 && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 space-y-1">
                {importPreview.errors.slice(0, 5).map((msg) => (
                  <p key={msg}>{msg}</p>
                ))}
                {importPreview.errors.length > 5 && (
                  <p>…and {importPreview.errors.length - 5} more issue(s)</p>
                )}
              </div>
            )}

            {importPreview.rows.length === 0 ? (
              <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700 font-medium">
                No valid cards found. Check your headers and try again.
              </div>
            ) : (
              <div className="rounded-xl border border-gray-200 overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0 z-10">
                      <tr>
                        <th className="text-left px-3 py-2 text-[10px] font-black uppercase tracking-wider text-gray-500 w-10">
                          #
                        </th>
                        <th className="text-left px-3 py-2 text-[10px] font-black uppercase tracking-wider text-gray-500">
                          Front
                        </th>
                        <th className="text-left px-3 py-2 text-[10px] font-black uppercase tracking-wider text-gray-500">
                          Back
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {importPreview.rows.slice(0, IMPORT_PREVIEW_ROWS).map((row, index) => (
                        <tr key={`${row.lineNumber}-${index}`} className="hover:bg-gray-50/80">
                          <td className="px-3 py-2 text-xs text-gray-400 align-top">{index + 1}</td>
                          <td className="px-3 py-2 text-gray-800 align-top break-words">{row.front}</td>
                          <td className="px-3 py-2 text-gray-600 align-top break-words">{row.back}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {importPreview.rows.length > IMPORT_PREVIEW_ROWS && (
                  <p className="text-xs text-center text-gray-500 font-medium py-2 bg-gray-50 border-t border-gray-100">
                    …and {importPreview.rows.length - IMPORT_PREVIEW_ROWS} more card
                    {importPreview.rows.length - IMPORT_PREVIEW_ROWS === 1 ? '' : 's'} not shown
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="mb-4 space-y-2">
          <p className="text-xs font-bold text-gray-500 uppercase">Add to deck</p>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              checked={importDeckMode === 'current'}
              onChange={() => setImportDeckMode('current')}
            />
            Current filter (
            {selectedDeckId
              ? decks.find((d) => d.id === selectedDeckId)?.name ?? 'deck'
              : 'Unassigned'}
            )
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              checked={importDeckMode === 'new'}
              onChange={() => setImportDeckMode('new')}
            />
            New deck:
            <input
              value={importNewDeckName}
              onChange={(e) => setImportNewDeckName(e.target.value)}
              className="flex-1 border rounded-lg px-2 py-1 text-sm"
              placeholder="Deck name"
            />
          </label>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={closeImportModal}
            className="flex-1 py-2.5 rounded-xl border font-medium text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!importPreview?.rows.length || isImporting}
            onClick={handleBulkImport}
            className={`flex-1 py-2.5 rounded-xl text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2 ${
              selectedTutee
                ? `bg-gradient-to-r ${selectedTutee.colorScheme.gradient}`
                : 'bg-indigo-600'
            }`}
          >
            {isImporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Import {importPreview?.rows.length ? `${importPreview.rows.length} cards` : ''}
              </>
            )}
          </button>
        </div>
      </FlashcardsFormModal>

      <FlashcardsFormModal open={showDeckModal} onClose={() => setShowDeckModal(false)}>
        <h3 className="font-black mb-3">New deck</h3>
        <input
          value={deckName}
          onChange={(e) => setDeckName(e.target.value)}
          className="w-full border rounded-xl px-3 py-2 mb-4"
          placeholder="Deck name"
        />
        <div className="flex gap-2">
          <button type="button" onClick={() => setShowDeckModal(false)} className="flex-1 py-2 rounded-xl border">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAddDeck}
            className={`flex-1 py-2 rounded-xl text-white font-bold ${
              selectedTutee
                ? `bg-gradient-to-r ${selectedTutee.colorScheme.gradient}`
                : 'bg-indigo-600'
            }`}
          >
            Create
          </button>
        </div>
      </FlashcardsFormModal>

      <FlashcardsFormModal open={showCardModal} onClose={() => setShowCardModal(false)}>
        <h3 className="font-black mb-3">New Card</h3>
        <label className="block text-[10px] font-black uppercase tracking-wider text-indigo-700 mb-1">
          Front — question / prompt
        </label>
        <textarea
          value={cardFront}
          onChange={(e) => setCardFront(e.target.value)}
          className="w-full border border-indigo-100 rounded-xl px-3 py-2 mb-3 min-h-[72px] focus:ring-2 focus:ring-indigo-500/25"
          placeholder="What the student sees first"
        />
        <label className="block text-[10px] font-black uppercase tracking-wider text-emerald-800 mb-1">
          Back — answer
        </label>
        <textarea
          value={cardBack}
          onChange={(e) => setCardBack(e.target.value)}
          className="w-full border border-emerald-100 rounded-xl px-3 py-2 mb-4 min-h-[72px] focus:ring-2 focus:ring-emerald-500/25"
          placeholder="Revealed after Show Answer"
        />
        <div className="flex gap-2">
          <button type="button" onClick={() => setShowCardModal(false)} className="flex-1 py-2 rounded-xl border">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAddCard}
            className={`flex-1 py-2 rounded-xl text-white font-bold ${
              selectedTutee
                ? `bg-gradient-to-r ${selectedTutee.colorScheme.gradient}`
                : 'bg-indigo-600'
            }`}
          >
            Add
          </button>
        </div>
      </FlashcardsFormModal>

      <ConfirmationModal
        isOpen={!!importSuccess}
        onClose={() => setImportSuccess(null)}
        onConfirm={() => setImportSuccess(null)}
        title="Import complete"
        message={
          importSuccess ? (
            <div className="space-y-2">
              <p>
                Successfully imported{' '}
                <strong>
                  {importSuccess.count} card{importSuccess.count === 1 ? '' : 's'}
                </strong>{' '}
                into <strong>{importSuccess.deckLabel}</strong>.
              </p>
              <p className="text-sm text-gray-500">Students can start studying them right away.</p>
            </div>
          ) : (
            ''
          )
        }
        confirmText="Done"
        cancelText="Close"
        type="success"
      />

      <ConfirmationModal
        isOpen={!!deleteDeckId}
        onClose={() => setDeleteDeckId(null)}
        onConfirm={async () => {
          if (deleteDeckId) {
            await deleteFlashcardDeck(deleteDeckId);
            setDeleteDeckId(null);
            load();
          }
        }}
        title="Delete deck?"
        message="Cards in this deck will become unassigned."
        confirmText="Delete"
        isDestructive
      />

      <ConfirmationModal
        isOpen={!!deleteCardId}
        onClose={() => setDeleteCardId(null)}
        onConfirm={async () => {
          if (deleteCardId) {
            await deleteFlashcard(deleteCardId);
            setDeleteCardId(null);
            load();
          }
        }}
        title="Delete card?"
        message="This cannot be undone."
        confirmText="Delete"
        isDestructive
      />
    </AnimatedCard>
    </div>
  );
};

export default FlashcardsConfig;
